/**
 * The worklist↔workbench round trip's provenance (GDN-3197), riding
 * react-router `location.state`.
 *
 * Outbound: a worklist row-click records WHERE the biller came from — the
 * full list URL (which, post GDN-3196, IS the saved list state) and a label
 * the workbench's back button can say truthfully. Inbound (back): the
 * workbench records WHICH claim was worked and the state it was last known
 * to be in, so the list can anchor the eye — flash the row when it's still
 * there, explain honestly when it left the view.
 *
 * Both readers are TOLERANT: history state is unowned data (it survives
 * refreshes, other code can write it), so anything malformed reads as
 * absent — and absence routes to the fallback, never a crash. Lost state
 * (true direct links, new tabs) is exactly when the fallback SHOULD apply.
 */

export interface WorkbenchFrom {
  /** Full list URL to return to, including the GDN-3196 params. */
  url: string;
  label: 'Claims' | 'Encounters';
}

export interface WorklistReturn {
  returnedClaimId: string;
  /** The claim's work-state label as the workbench last knew it (its
   *  `work_summary`) — null when no canonical row was available. */
  returnedStateLabel: string | null;
}

/**
 * The no-provenance default (decided): the workbench is the claim tool, so
 * its home is the claim list — landing billers on Encounters was an accident
 * of /jotbill's default tab, not a decision.
 */
export const WORKBENCH_BACK_FALLBACK = {
  url: '/jotbill?tab=claims',
  label: 'Claims worklist',
} as const;

export function workbenchProvenanceState(from: WorkbenchFrom): { workbenchFrom: WorkbenchFrom } {
  return { workbenchFrom: from };
}

export function readWorkbenchFrom(state: unknown): WorkbenchFrom | null {
  if (!state || typeof state !== 'object') return null;
  const from = (state as Record<string, unknown>).workbenchFrom;
  if (!from || typeof from !== 'object') return null;
  const { label, url } = from as Record<string, unknown>;
  /* Same-origin paths only — decided by the SAME algorithm the browser
     uses, not by string patterns. Three review rounds each found one more
     WHATWG quirk that makes a '/'-prefixed string resolve cross-origin
     (`//host`, `\` treated as `/`, tab/newline stripped before parsing),
     where pushState throws a SecurityError on back-click. Pattern checks
     can't close that class; parsing with `new URL` against the current
     origin does, because whatever quirk exists, the verdict here and the
     resolution at navigate-time agree by construction. The explicit
     backslash/control-character reject stays as belt and braces — no
     legitimate list URL contains them. */
  if (typeof url !== 'string' || !url.startsWith('/') || /[\\\t\n\r]/.test(url)) return null;
  try {
    if (new URL(url, window.location.origin).origin !== window.location.origin) return null;
  } catch {
    return null;
  }
  if (label !== 'Claims' && label !== 'Encounters') return null;
  return { url, label };
}

export function worklistReturnState(returned: WorklistReturn): { worklistReturn: WorklistReturn } {
  return { worklistReturn: returned };
}

export function readWorklistReturn(state: unknown): WorklistReturn | null {
  if (!state || typeof state !== 'object') return null;
  const returned = (state as Record<string, unknown>).worklistReturn;
  if (!returned || typeof returned !== 'object') return null;
  const { returnedClaimId, returnedStateLabel } = returned as Record<string, unknown>;
  if (typeof returnedClaimId !== 'string' || !returnedClaimId) return null;
  return {
    returnedClaimId,
    returnedStateLabel: typeof returnedStateLabel === 'string' ? returnedStateLabel : null,
  };
}
